import ReactDOM from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";
import "./index.css";
import { Spinner } from "./components/ui/spinner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { DefaultRouterErrorComponent } from "./components/root-error";
import { asError } from "./lib/thrown-error";
import { env } from "./env";
import { shouldRetryQuery } from "./lib/query-retry";
import { installStaleChunkReload } from "@merchant-portal-backend/ui/lib/stale-chunk-reload";

installStaleChunkReload();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: shouldRetryQuery,
    },
  },
});

// Set up a Router instance
const router = createRouter({
  routeTree,
  context: {
    queryClient,
  },
  /**
   * This potentially can lead us to flood the backend with requests.
   * For now lets try without it and we can turn it back on if we need to.
   *
   * This means that whenever you hover over a link that would need to call a `loader` it prefetches the data into memory so the page opens instantly.
   */
  // defaultPreload: "intent",
  defaultPendingComponent: () => <Spinner />,
  defaultErrorComponent: ({ error, reset }) => {
    return <DefaultRouterErrorComponent error={asError(error)} reset={reset} />;
  },
});

// Register things for typesafety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const rootElement = document.getElementById("app")!;

// Runs the request mocking if dev
async function enableMocking() {
  if (!env.VITE_IS_DEMO && import.meta.env.VITE_MOCKING === "false") {
    console.log("❌ API Mocking Disabled");
    return;
  }

  if (!env.VITE_IS_DEMO && !import.meta.env.DEV) {
    console.log("❌ API Mocking Disabled");
    return;
  }
  console.log("✅ API Mocking Enabled");

  const { worker } = await import("./mocks/browser");
  return worker.start({ onUnhandledRequest: "error" });
}

if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  enableMocking().then(() => {
    root.render(
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>,
    );
  });
}
